import { useMemo } from "react";
import { Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import { formatVolume } from "@jackedlogs/shared";

import { patternLabel } from "../patternLabel";
import { PanelTitleRow } from "./InfoPill";
import { ErrorCard, LoadingCard } from "./panelCards";
import { makeTierOneStyles } from "./panelStyles";
import { DEFAULT_WINDOW_DAYS } from "./progressWindow";
import { computeMovementBalance } from "./tierOneMath";
import type { ProgressDeps } from "./types";
import { useSessionResultsFromDeps } from "./useSessionResults";

// Sets per movement pattern, 7d + 28d.
export function MovementBalancePanel(deps: ProgressDeps) {
  const { theme, units } = deps;
  const windowDays = deps.windowDays ?? DEFAULT_WINDOW_DAYS;
  const { t, i18n } = useTranslation();
  const { results, catalog, error } = useSessionResultsFromDeps(deps);
  const styles = useMemo(() => makeTierOneStyles(theme), [theme]);

  const data = useMemo(() => {
    if (!results || !catalog) return null;
    return computeMovementBalance(results, catalog, Date.now(), windowDays);
  }, [results, catalog, windowDays]);

  if (error) return <ErrorCard theme={theme} titleKey="balance" />;
  if (data === null) return <LoadingCard theme={theme} titleKey="balance" />;

  const balanceTitle = t("mobile.progress.balance.title", {
    defaultValue: "Movement balance",
  });
  const balanceHelp = t("mobile.progress.balance.help", {
    days: windowDays,
    defaultValue:
      'Sets and volume per movement pattern over the selected {{days}}-day window, sorted by sets. The small "X/7d" number is the last-7-day count, useful to spot a single recent push (or a deliberate drop) that the longer window hides.',
  });
  const period = t("progress.period.days", {
    defaultValue: "{{days}} d",
    days: windowDays,
  });

  if (data.length === 0) {
    return (
      <View style={styles.card}>
        <PanelTitleRow
          theme={theme}
          title={balanceTitle}
          body={balanceHelp}
          period={period}
        />
        <Text style={styles.empty}>
          {t("mobile.progress.empty", {
            defaultValue: "No recent activity to compare",
          })}
        </Text>
      </View>
    );
  }

  const max = Math.max(...data.map((r) => r.setsWindow));

  return (
    <View style={styles.card}>
      <PanelTitleRow
        theme={theme}
        title={balanceTitle}
        body={balanceHelp}
        period={period}
      />
      {data.map((row) => {
        const fillPct = max === 0 ? 0 : (row.setsWindow / max) * 100;
        return (
          <View key={row.pattern} style={styles.balanceRow}>
            <View style={styles.balanceTextRow}>
              <Text style={styles.balanceLabel} numberOfLines={1}>
                {patternLabel(t, row.pattern)}
              </Text>
              <Text style={styles.balanceValue}>
                {row.setsWindow} · {row.sets7d}/7d ·{" "}
                {formatVolume(row.volumeWindowKg, units, i18n.language)}
              </Text>
            </View>
            <View style={styles.barTrack}>
              <View
                style={[styles.barFill, { width: `${Math.max(2, fillPct)}%` }]}
              />
            </View>
          </View>
        );
      })}
    </View>
  );
}
